import React from 'react';
import { Send, UserCheck, UserList, AddList } from '@saleshandy/icons';
import { OverlayTooltip } from '../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import { V2Lead } from '../../../types';
import { hasLeadNoEmail, hasLeadValidEmail } from '../../../helpers/helper';

interface ActionsCellProps {
  lead: V2Lead;
  onSaveAsProspect: (leadId: number) => void;
  onAddToSequence: (leadId: number) => void;
  onAddToList?: (leadId: number) => void;
  isLoading?: boolean;
  isLoadingAddToProspect?: boolean;
  isLoadingAddToSequence?: boolean;
  isAnyRevealing?: boolean;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  lead,
  onSaveAsProspect,
  onAddToSequence,
  onAddToList,
  isLoading = false,
  isLoadingAddToProspect = false,
  isLoadingAddToSequence = false,
  isAnyRevealing = false,
}) => {
  const isRevealing =
    lead.isRevealing === true ||
    lead.isPersonalEmailRevealing === true ||
    isAnyRevealing;
  const hasNoEmails = hasLeadNoEmail(lead);
  const isRevealedWithNoEmail =
    lead.isRevealed === true && !hasLeadValidEmail(lead);
  const isSaveDisabled =
    !lead.isRevealed ||
    hasNoEmails ||
    isRevealedWithNoEmail ||
    isLoading ||
    isLoadingAddToProspect ||
    isRevealing;
  const isSequenceDisabled =
    !lead.isRevealed ||
    hasNoEmails ||
    isRevealedWithNoEmail ||
    isLoading ||
    isLoadingAddToSequence ||
    isRevealing;
  const isAddToListDisabled = isRevealing;

  const getSaveAsProspectTooltip = () => {
    if (isRevealing) {
      return 'Enriching...';
    }
    if (lead.isProspectCreated) {
      return 'Prospect Saved';
    }
    if (hasNoEmails || isRevealedWithNoEmail) {
      return 'Lead does not have any email address';
    }
    if (isLoading) {
      return 'Saving Prospect...';
    }
    return 'Save as Prospect';
  };

  const getAddToSequenceTooltip = () => {
    if (isRevealing) {
      return 'Enriching...';
    }
    if (hasNoEmails || isRevealedWithNoEmail) {
      return 'Lead does not have any email address';
    }
    if (isLoadingAddToSequence) {
      return 'Adding to Sequence...';
    }
    return 'Add to Sequence';
  };

  return (
    <div className="v2-lead-actions-cell">
      <OverlayTooltip text={getSaveAsProspectTooltip()}>
        <button
          type="button"
          className="v2-lead-action-btn"
          disabled={isSaveDisabled}
          {...accessibleOnClick(
            () =>
              !isSaveDisabled &&
              !lead.isProspectCreated &&
              onSaveAsProspect(lead.id),
          )}
        >
          {isLoadingAddToProspect && (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          )}
          {!isLoadingAddToProspect && lead.isProspectCreated && (
            <UserCheck width={16} height={16} />
          )}
          {!isLoadingAddToProspect && !lead.isProspectCreated && (
            <UserList width={16} height={16} />
          )}
        </button>
      </OverlayTooltip>
      <OverlayTooltip text={getAddToSequenceTooltip()}>
        <button
          type="button"
          className="v2-lead-action-btn"
          disabled={isSequenceDisabled}
          {...accessibleOnClick(
            () => !isSequenceDisabled && onAddToSequence(lead.id),
          )}
        >
          {isLoadingAddToSequence ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            <Send width={16} height={16} />
          )}
        </button>
      </OverlayTooltip>

      <OverlayTooltip text={isRevealing ? 'Enriching...' : 'Save to List'}>
        <button
          type="button"
          className="v2-lead-action-btn"
          disabled={isAddToListDisabled}
          {...accessibleOnClick(() => {
            if (!isAddToListDisabled) onAddToList?.(lead.id);
          })}
        >
          <AddList width={16} height={16} />
        </button>
      </OverlayTooltip>
    </div>
  );
};

// Memoize ActionsCell to prevent unnecessary re-renders
export default React.memo(
  ActionsCell,
  (prevProps, nextProps) =>
    prevProps.lead.id === nextProps.lead.id &&
    prevProps.lead.isRevealed === nextProps.lead.isRevealed &&
    prevProps.lead.isRevealing === nextProps.lead.isRevealing &&
    prevProps.lead.isPersonalEmailRevealing ===
      nextProps.lead.isPersonalEmailRevealing &&
    prevProps.lead.isProspectCreated === nextProps.lead.isProspectCreated &&
    JSON.stringify(prevProps.lead.emails) ===
      JSON.stringify(nextProps.lead.emails) &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isLoadingAddToProspect === nextProps.isLoadingAddToProspect &&
    prevProps.isLoadingAddToSequence === nextProps.isLoadingAddToSequence &&
    prevProps.isAnyRevealing === nextProps.isAnyRevealing &&
    prevProps.onSaveAsProspect === nextProps.onSaveAsProspect &&
    prevProps.onAddToSequence === nextProps.onAddToSequence &&
    prevProps.onAddToList === nextProps.onAddToList,
);
